import { useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { NavLink } from '@/components/NavLink';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Menu,
  LogOut,
  Plane,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '@/features/auth/context/AuthContext';
import capIcon from '@/assets/cap_icon_only.svg';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Viaggi', href: '/viaggi', icon: Plane },
  { name: 'Questionari', href: '/questionari', icon: FileText },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { logout, user } = useAuth();

  const NavItems = () => (
    <>
      {navigation.map((item) => {
        const isActive =
          item.href === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.href);
        return (
          <NavLink
            key={item.name}
            to={item.href}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth ${isActive
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'text-foreground hover:bg-muted'
              }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <item.icon
              className={`h-5 w-5 ${isActive ? '' : 'text-capviaggi-blue'}`}
            />
            <span className="font-medium">{item.name}</span>
          </NavLink>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-white backdrop-blur supports-[backdrop-filter]:bg-white/95">
        <div className="container flex h-16 items-center justify-between px-4 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
              <img src={capIcon} alt="Capviaggi" className="h-6 w-6" />
            </div>
            <span className="text-lg font-bold text-foreground">
              Survey System
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2">
            <NavItems />

            {/* Guida */}
            <a
              href="/guida.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              title="Guida"
            >
              <BookOpen className="h-5 w-5" />
            </a>

            {/* User Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-4 flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                  <Avatar className="h-8 w-8 bg-gradient-to-br from-capviaggi-blue to-capviaggi-blue/80 border border-capviaggi-blue/20 shadow-sm">
                    <AvatarFallback className="bg-transparent text-white text-xs font-bold">
                      {user?.initials || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-foreground leading-none">
                      {user?.name || 'Utente'}
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                      {user?.email || ''}
                    </span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={logout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <div className="flex flex-col gap-2 mt-8">
                {/* User Profile in Mobile */}
                <div className="flex items-center gap-3 px-4 py-3 mb-2 bg-muted/30 rounded-lg">
                  <Avatar className="h-10 w-10 bg-gradient-to-br from-capviaggi-blue to-capviaggi-blue/80 border border-capviaggi-blue/20">
                    <AvatarFallback className="bg-transparent text-white text-sm font-bold">
                      {user?.initials || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {user?.name || 'Utente'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user?.email || ''}
                    </span>
                  </div>
                </div>

                <NavItems />

                <a
                  href="/guida.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors mt-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <BookOpen className="h-5 w-5 text-capviaggi-blue" />
                  <span className="font-medium">Guida</span>
                </a>

                <Button
                  variant="ghost"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-destructive hover:text-destructive hover:bg-destructive/10 justify-start mt-2"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Esci</span>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
